import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useParams, useNavigate } from 'react-router-dom';

import { ButtonLink } from '../../components/general';
import {
  PageWrapper,
  ContentHeader,
  BackBtn,
} from '../../components/layout/content';

import { useWindowWidth } from '../../hooks/useWindowWidth';

import { getHelpUrl, getBpWidth } from '../../utils';

import HelpForm from './components/helpForm';
import { HelpTopic } from './components/types';
import { helpContent } from './content';

const Help = () => {
  const { topicSlug } = useParams();

  const navigate = useNavigate();

  const windowWidth = useWindowWidth();
  const isMobileDevice = useMemo(
    () => windowWidth < getBpWidth('lg'),
    [windowWidth],
  );

  const articles = useMemo(() => {
    return helpContent.find((topic) => topic.slug === topicSlug)?.articles;
  }, [topicSlug]);

  // set topic if not given
  useEffect(() => {
    if (!topicSlug && !isMobileDevice) navigate(getHelpUrl('attendee'));
  }, [navigate, topicSlug, isMobileDevice]);

  const renderTopicButton = (topic: HelpTopic) => {
    const isActive = topic.slug === topicSlug;

    return (
      <ButtonLink
        key={topic.slug}
        to={getHelpUrl(topic.slug)}
        variant={isMobileDevice ? 'primary' : 'primary-thin'}
        colour={
          isMobileDevice ? 'custom' : isActive ? 'primary' : 'transparent-grey'
        }
        className={classNames(
          'w-full text-md text-left',
          isMobileDevice &&
            'px-[5vw] first:border-t border-b border-grey-200 border-solid rounded-none',
          !isMobileDevice && (isActive ? 'font-bold' : 'font-medium'),
        )}
      >
        <div className="w-full">{topic.name}</div>
      </ButtonLink>
    );
  };

  return (
    <PageWrapper type="generic" title="Help" className="gap-6">
      <ContentHeader title="Help Page" />

      <div className="flex flex-col gap-4">
        <h5 className="hidden lg:block">All help topics</h5>

        <div className="flex flex-col lg:flex-row lg:gap-16">
          {isMobileDevice && topicSlug ? (
            <BackBtn
              onClick={() => navigate(getHelpUrl())}
              label="All Topics"
            />
          ) : (
            <div className="w-full lg:w-1/5 flex flex-col lg:gap-2">
              {helpContent.map((topic) => renderTopicButton(topic))}
            </div>
          )}

          <div className="w-full lg:w-4/5 flex flex-wrap gap-6 lg:gap-8">
            {articles?.map((article) => (
              <ButtonLink
                key={article.slug}
                to={getHelpUrl(topicSlug, article.slug)}
                variant="custom"
                colour="grey-outline"
                className="w-full lg:w-[calc((100%_-_2rem)_/_2)] flex flex-col p-4 gap-4 text-black rounded-md"
              >
                <h5>{article.content.title}</h5>

                <div className="text-sm">{article.content.subtitle}</div>
              </ButtonLink>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:h-4" />

      <div className="w-full sm:w-2/3 self-center">
        <HelpForm />
      </div>
    </PageWrapper>
  );
};

export default Help;
